/**
 * VH-101: Recipe-level cover image uploader.
 *
 * Drag-and-drop OR click-to-pick a single image. Replacing an existing image
 * shows a confirm dialog. Validation (MIME + size) is enforced client-side
 * to avoid wasting bytes on the wire; the server re-validates regardless.
 */

import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  PhotoIcon,
  ArrowUpTrayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import {
  useUploadCoverImage,
  useDeleteCoverImage,
} from '../../hooks/useRecipes'

const ACCEPTED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
const MAX_BYTES = 10 * 1024 * 1024

export default function RecipeCoverUploader({ recipe }) {
  const { t } = useTranslation()
  const inputRef = useRef(null)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState(null)

  const uploadMutation = useUploadCoverImage(recipe.id)
  const deleteMutation = useDeleteCoverImage(recipe.id)
  const busy = uploadMutation.isPending || deleteMutation.isPending

  const currentUrl = recipe.cover_image_url || null

  const validate = (file) => {
    if (!file) return t('recipes.cover.errorUpload')
    if (!ACCEPTED.includes(file.type)) return t('recipes.photo.wrongFormat')
    if (file.size > MAX_BYTES) return t('recipes.photo.fileTooLarge')
    return null
  }

  const upload = (file) => {
    const fd = new FormData()
    fd.append('file', file)
    setError(null)
    uploadMutation.mutate(fd, {
      // A 401 here means the silent token-refresh already failed and the
      // session is dead (the global interceptor has emitted session-expired).
      // Surface a re-login prompt instead of a misleading "upload failed".
      onError: (err) =>
        setError(
          err?.response?.status === 401
            ? t('recipes.cover.errorSessionExpired')
            : t('recipes.cover.errorUpload')
        ),
    })
  }

  const handleFile = (file) => {
    const err = validate(file)
    if (err) {
      setError(err)
      return
    }
    if (currentUrl) {
      // Replace flow — confirm before overwriting.
      const ok = window.confirm(t('recipes.cover.confirmReplace'))
      if (!ok) return
    }
    upload(file)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    setDragOver(false)
    const file = event.dataTransfer?.files?.[0]
    handleFile(file)
  }

  const handleRemove = () => {
    setError(null)
    deleteMutation.mutate()
  }

  return (
    <div className="space-y-2" data-testid="recipe-cover-uploader">
      <div className="text-sm font-medium text-gray-900">
        {t('recipes.cover.sectionTitle')}
      </div>

      {currentUrl ? (
        <div className="relative">
          <img
            src={currentUrl}
            alt="recipe cover"
            className="w-full max-h-72 object-cover rounded-lg border border-gray-200"
            data-testid="recipe-cover-preview"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              data-testid="recipe-cover-replace"
            >
              <ArrowUpTrayIcon className="h-3.5 w-3.5" />
              {busy
                ? t('recipes.cover.uploading')
                : t('recipes.cover.replace')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={handleRemove}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
              data-testid="recipe-cover-remove"
            >
              <TrashIcon className="h-3.5 w-3.5" />
              {t('recipes.cover.remove')}
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => !busy && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer ${
            dragOver
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 hover:border-gray-400 bg-gray-50'
          } ${busy ? 'opacity-60 cursor-wait' : ''}`}
          data-testid="recipe-cover-dropzone"
        >
          <PhotoIcon className="mx-auto h-10 w-10 text-gray-400" />
          <p className="mt-2 text-sm text-gray-700">
            {busy
              ? t('recipes.cover.uploading')
              : t('recipes.cover.dropZone')}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {t('recipes.cover.supported')}
          </p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(',')}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="recipe-cover-file-input"
      />

      {error && (
        <p className="text-sm text-red-600" data-testid="recipe-cover-error">
          {error}
        </p>
      )}
    </div>
  )
}
